import type { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ColumnFilterCell } from './ColumnFilterCell';
import { SortableHeader } from './SortableHeader';
import type { DataTableColumn } from './types';

interface DataTableProps<T, Ctx> {
  /** Rows already sorted + filtered (from useXxxListTable.sortedItems). */
  items: T[];
  /** Visible column defs (from useXxxListTable.visibleColumns). */
  columns: DataTableColumn<T, Ctx>[];
  /** Render-context passed to each column's getValue/render. */
  ctx: Ctx;

  /** Per-column filter values keyed by column.key. */
  columnFilters: Record<string, string>;
  /** Active sort key (matches one of the columns' sortKey). */
  sortKey: string;
  /** 1 = asc, -1 = desc. */
  sortDirection: 1 | -1;

  /** Called when a sortable header is clicked. */
  onSort: (key: string) => void;
  /** Called when a filter cell changes. */
  onSetColumnFilter: (key: string, value: string) => void;

  /** Row click handler. If omitted, rows aren't clickable. */
  onRowClick?: (item: T) => void;
  /** Returns a stable row key (defaults to `String((item as {id:string}).id)`). */
  getRowKey?: (item: T) => string;

  /** Loading state renders skeleton rows. */
  isLoading?: boolean;
  /** Rendered inside the empty-state row when items.length === 0 && !isLoading. */
  emptyState?: ReactNode;

  /** Optional className for the outer card container. */
  className?: string;
}

/**
 * Canonical read-only data table for the atek-frontend-layout list pattern.
 *
 * Covers: rounded-lg border bg-card container, full-bg-muted filter row,
 * bg-white header row, sortable headers, primary-search / multi-select / text
 * filter cells, row click, skeleton loading, empty state.
 *
 * Does NOT cover: inline cell editing, column pivoting, sticky first column,
 * drag-to-reorder. Those are KPI-scorecard concerns — keep that component
 * bespoke.
 */
export function DataTable<T, Ctx = unknown>({
  items,
  columns,
  ctx,
  columnFilters,
  sortKey,
  sortDirection,
  onSort,
  onSetColumnFilter,
  onRowClick,
  getRowKey,
  isLoading,
  emptyState,
  className,
}: DataTableProps<T, Ctx>) {
  const keyOf = getRowKey ?? ((item: T) => String((item as { id: string }).id));

  return (
    <div className={cn('rounded-lg border bg-card overflow-x-auto', className)}>
      {/*
        Dense-table density per frontend-react-layout skill:
          - `text-xs` cells (12 px) — `[&_td]/[&_th]` selectors beat per-cell `text-sm`
          - `[&_td]:py-1` shrinks row padding from 8 px → 4 px (half-height rows)
          - `[&_th]:h-8` shrinks header from 40 px → 32 px
        Per-cell horizontal padding stays at the shadcn default `px-2`.
      */}
      <Table className="text-xs [&_td]:text-xs [&_th]:text-xs [&_td]:py-1 [&_th]:h-8">
        <TableHeader>
          {/* Filter row */}
          <TableRow className="bg-muted hover:bg-muted">
            {columns.map((col) => (
              <ColumnFilterCell
                key={`filter-${col.key}`}
                col={col}
                columnFilters={columnFilters}
                setColumnFilter={onSetColumnFilter}
              />
            ))}
          </TableRow>
          {/* Column label row */}
          <TableRow className="hover:bg-transparent bg-white">
            {columns.map((col) => (
              <TableHead key={col.key}>
                {col.sortKey ? (
                  <SortableHeader
                    label={col.label}
                    sortKey={col.sortKey}
                    currentSortKey={sortKey}
                    sortDirection={sortDirection}
                    onSort={onSort}
                  />
                ) : (
                  col.label
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading &&
            Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))}

          {!isLoading && items.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="py-12 text-center text-muted-foreground"
              >
                {emptyState ?? 'No results.'}
              </TableCell>
            </TableRow>
          )}

          {!isLoading &&
            items.map((item) => (
              <TableRow
                key={keyOf(item)}
                className={cn(onRowClick && 'cursor-pointer hover:bg-muted/50')}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    className={cn('text-sm', col.className)}
                  >
                    {col.render
                      ? col.render(item, ctx)
                      : String(col.getValue(item, ctx) ?? '') || '—'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
}
